import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search, ArrowRight } from "lucide-react";
import { useSiteTexts } from "../../hooks/useSiteTexts";


const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const LOCATIONS = ["Cannes", "Antibes", "Nice", "Mougins", "Juan-les-Pins", "Saint-Raphaël", "Saint-Jean-Cap-Ferrat"];
const BUDGETS = [
  { value: "", label: "Budget max" },
  ...Array.from({ length: 10 }, (_, i) => {
    const v = (i + 1) * 100000;
    return { value: String(v), label: `≤ ${v.toLocaleString("fr-FR")} €` };
  }),
  { value: "min1000000", label: "Plus de 1 000 000 €" },
];

const selectCls =
  "w-full bg-transparent text-sm font-light text-azur outline-none placeholder:text-slate-400";

export default function Hero() {
  const t = useSiteTexts();
  const navigate = useNavigate();
  const [mode, setMode] = useState("acheter");
  const [type, setType] = useState("");
  const [location, setLocation] = useState("");
  const [budget, setBudget] = useState("");

  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 900], [0, 220]);
  const imgScale = useTransform(scrollY, [0, 900], [1.08, 1.25]);

  const submit = (e) => {
    e.preventDefault();
    if (mode === "vendre") {
      navigate("/vendre");
      return;
    }
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (location) params.set("location", location);
    if (budget) {
      if (budget.startsWith("min")) params.set("min_budget", budget.slice(3));
      else params.set("max_budget", budget);
    }
    navigate(`/biens?${params.toString()}`);
  };

  return (
    <section data-testid="hero-section" className="relative flex flex-col justify-end overflow-hidden bg-[#0A0A0A] sm:min-h-screen">
      <motion.div
        className="relative aspect-[4/5] w-full sm:absolute sm:inset-0 sm:aspect-auto"
        style={{ y: imgY, scale: imgScale }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
      >
        <video
          poster="/hero-nice.jpg"
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-video"
          className="h-full w-full object-cover"
        >
          <source src="/hero-loop.mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 hidden bg-gradient-to-t from-slate-950/85 via-slate-950/40 to-slate-950/25 sm:block" />

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-10 pt-8 text-center sm:px-8 sm:pb-8 sm:pt-40 lg:px-16">
        <h1 className="mt-4 font-serif text-3xl font-light leading-[1.15] tracking-tight text-white sm:mt-6 sm:text-4xl lg:text-5xl">
          <MaskedLine delay={0.4}>
            <span>
              {t("hero_title_1", "Votre projet immobilier,")}{" "}
              <span className="italic text-gold">{t("hero_title_2", "notre engagement.")}</span>
            </span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.75} className="mx-auto mt-5 max-w-2xl">
          <p className="font-serif text-lg font-light italic leading-relaxed text-white/85 sm:text-xl">
            {t("hero_subtitle", "Acheter, vendre, investir sur la Côte d'Azur avec un accompagnement sur mesure.")}
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href="#nos-biens"
            data-testid="hero-cta-biens"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-azur transition-all duration-300 hover:bg-gold"
          >
            Découvrir nos biens
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <button
            onClick={() => navigate("/vendre")}
            data-testid="hero-cta-estimation"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-medium text-white backdrop-blur-sm transition-all duration-300 hover:border-gold hover:text-gold"
          >
            Estimer mon bien
          </button>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="hero-search-form"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 rounded-2xl border border-white/40 bg-white/80 p-3 shadow-[0_20px_60px_rgba(12,35,64,0.25)] backdrop-blur-xl backdrop-saturate-150"
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex rounded-full bg-sand p-1 lg:shrink-0">
              {["acheter", "vendre"].map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`hero-search-tab-${m}`}
                  onClick={() => setMode(m)}
                  className={`relative flex-1 rounded-full px-5 py-2 text-sm font-medium capitalize transition-colors duration-300 lg:flex-none ${
                    mode === m ? "text-white" : "text-azur hover:text-terracotta"
                  }`}
                >
                  {mode === m && (
                    <motion.span
                      layoutId="hero-tab-pill"
                      className="absolute inset-0 rounded-full bg-azur"
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                    />
                  )}
                  <span className="relative">{m === "acheter" ? "Acheter" : "Vendre"}</span>
                </button>
              ))}
            </div>

            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="rounded-xl border border-slate-200 bg-white/60 px-4 py-2.5">
                <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Type de bien</span>
                <select data-testid="hero-search-type" value={type} onChange={(e) => setType(e.target.value)} className={selectCls}>
                  <option value="">Tous types</option>
                  <option value="Villa">Villa</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Maison">Maison</option>
                  <option value="Local commercial">Local commercial</option>
                </select>
              </label>
              <label className="rounded-xl border border-slate-200 bg-white/60 px-4 py-2.5">
                <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Localisation</span>
                <select data-testid="hero-search-location" value={location} onChange={(e) => setLocation(e.target.value)} className={selectCls}>
                  <option value="">Toute la Côte d'Azur</option>
                  {LOCATIONS.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </label>
              <label className="rounded-xl border border-slate-200 bg-white/60 px-4 py-2.5">
                <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Budget</span>
                <select data-testid="hero-search-budget" value={budget} onChange={(e) => setBudget(e.target.value)} className={selectCls}>
                  {BUDGETS.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <button
              type="submit"
              data-testid="hero-search-submit"
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-azur px-7 py-4 text-sm font-medium text-sand transition-colors duration-300 hover:bg-terracotta lg:shrink-0"
            >
              <Search size={16} />
              Rechercher
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
